import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDateRange, DateRangePreset } from './DateRangeContext';
import { cn } from "@/lib/utils";

const presetLabels: Record<DateRangePreset, string> = {
  'YTD': 'Year to Date',
  '2025': '2025',
  'custom': 'Custom Range'
};

export const DateRangeFilter: React.FC = () => {
  const { dateRange, setDateRange, getPresetRange, setSelectedYear } = useDateRange();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState<Date | undefined>(dateRange.from);
  const [customTo, setCustomTo] = useState<Date | undefined>(dateRange.to);

  const handlePresetSelect = (preset: DateRangePreset) => {
    console.log('Selected preset:', preset);
    if (preset === 'custom') {
      console.log('Opening calendar...');
      setIsCalendarOpen(true);
      return;
    }

    const newRange = getPresetRange(preset);
    console.log('Setting new range:', newRange);
    setDateRange(newRange);

    // Keep "selectedYear" in sync for components that depend on it
    if (preset === 'YTD') {
      setSelectedYear(new Date().getFullYear());
    }
    if (preset === '2025') {
      setSelectedYear(2025);
    }
  };

  const handleCustomRangeApply = () => {
    console.log('Applying custom range:', { customFrom, customTo });
    if (customFrom && customTo) {
      setDateRange({
        from: customFrom,
        to: customTo,
        preset: 'custom'
      });
      // Best-effort: keep selectedYear aligned to the custom range end year
      setSelectedYear(customTo.getFullYear());
      setIsCalendarOpen(false);
    }
  };

  const formatDateRange = () => {
    if (dateRange.preset !== 'custom') {
      return presetLabels[dateRange.preset];
    }
    return `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 bg-background">
            <Calendar className="h-4 w-4" />
            <span>{formatDateRange()}</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48 bg-popover border border-border shadow-md z-50"
        >
          {(Object.keys(presetLabels) as DateRangePreset[]).map((preset) => (
            <DropdownMenuItem
              key={preset}
              onClick={() => handlePresetSelect(preset)}
              className={cn(
                "cursor-pointer hover:bg-accent",
                dateRange.preset === preset && "bg-accent"
              )}
            >
              {presetLabels[preset]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-3">From</div>
              <CalendarComponent
                mode="single"
                selected={customFrom}
                onSelect={setCustomFrom}
                className="rounded-md border bg-background pointer-events-auto"
              />
            </div>
            
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-3">To</div>
              <CalendarComponent
                mode="single"
                selected={customTo}
                onSelect={setCustomTo}
                className="rounded-md border bg-background pointer-events-auto"
                disabled={(date) => 
                  customFrom ? date < customFrom : false
                }
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setIsCalendarOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCustomRangeApply}
              disabled={!customFrom || !customTo}
            >
              Apply Range
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};